import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X as XIcon } from './common/Icon';
import { openDeepLink } from '../hooks/useGlobalNativeMessage';
import { visitorSheetBus } from '../utils/visitorSheetBus';
import { COLORS, GRADIENTS, RADII, SHADOWS, SPACING, FONT_SIZES, FONT_WEIGHTS, Z_INDEX } from '../styles/theme';

interface BannerData {
  id: number;
  title: string;
  body: string;
  deepLink: string;
  extra: any;
}

const AUTO_HIDE_MS = 5000;
const EXIT_MS = 300;

// Shown for any push that arrives while the app is open. It never navigates on
// its own; the user taps it to open the deep link. Sits just below the
// full-screen overlays (visitor approval / SOS), which use Z_INDEX.toast.
const GlobalPushBanner: React.FC = () => {
  const navigate = useNavigate();
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [leaving, setLeaving] = useState(false);
  const idRef = useRef(0);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Slide the banner back up, then unmount it. Used by the auto-hide, the X and a tap.
  const dismiss = useCallback(() => {
    setLeaving(true);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => { setBanner(null); setLeaving(false); }, EXIT_MS);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (!d?.deepLink) return;
      // Case 9: the visit is already open in a details sheet, which shows the update
      // itself (it refreshes on `nativePushReceived`) — no duplicate banner on top.
      if ((d.deepLink === '/visitor' || d.deepLink === '/approval') && d.extra?.visitId
          && visitorSheetBus.isOpen({ visitId: d.extra.visitId })) return;
      idRef.current += 1;
      setBanner({ id: idRef.current, title: d.title, body: d.body, deepLink: d.deepLink, extra: d.extra });
      // A newer push replaces the one on screen; keying the element by id replays the drop-in.
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setLeaving(false);
    };
    window.addEventListener('inAppPushBanner', handler);
    return () => {
      window.removeEventListener('inAppPushBanner', handler);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(dismiss, AUTO_HIDE_MS);
    return () => clearTimeout(t);
  }, [banner, dismiss]);

  const handleOpen = useCallback(() => {
    if (!banner) return;
    // Same shape Flutter delivers, so page listeners of `nativeDeepLink` parse it as for a tap
    const rawData = { data: { deepLink: banner.deepLink, extra: { deepLink: banner.deepLink, extra: banner.extra } } };
    openDeepLink(navigate, banner.deepLink, banner.extra, rawData);
    setBanner(null);
    setLeaving(false);
  }, [banner, navigate]);

  if (!banner) return null;

  return (
    <div
      key={banner.id}
      className={leaving ? 'push-banner-out' : 'push-banner-in'}
      onClick={handleOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpen(); }
      }}
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        left: SPACING.md, right: SPACING.md,
        maxWidth: 420, margin: '0 auto',
        display: 'flex', alignItems: 'center', gap: SPACING.md,
        padding: `${SPACING.md}px ${SPACING.lg}px`,
        background: COLORS.bgCard,
        borderRadius: RADII.xxl,
        border: `1px solid ${COLORS.border}`,
        borderLeft: `4px solid ${COLORS.gold}`,
        boxShadow: SHADOWS.elevated,
        cursor: 'pointer',
        zIndex: Z_INDEX.toast - 1,
        overflow: 'hidden',
      }}
    >
      <div aria-hidden style={{
        width: 36, height: 36, borderRadius: 12, flexShrink: 0,
        background: '#f7efd9', color: '#8a6d28',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'pushIconPulse 1.6s ease-out 0.5s 2',
      }}>
        <Bell size={18} strokeWidth={1.9} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: FONT_SIZES.md, fontWeight: FONT_WEIGHTS.bold, color: COLORS.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{banner.title}</div>
        {banner.body && (
          <div style={{
            fontSize: FONT_SIZES.sm, color: COLORS.textSecondary, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{banner.body}</div>
        )}
      </div>
      <button
        aria-label="Dismiss"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        style={{
          background: 'none', border: 'none', padding: SPACING.xs, cursor: 'pointer',
          color: COLORS.textMuted, display: 'flex', flexShrink: 0,
        }}
      >
        <XIcon size={16} strokeWidth={2} />
      </button>
      {/* Time left before the banner hides itself */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
        background: GRADIENTS.gold, transformOrigin: 'left',
        animation: `pushBannerCountdown ${AUTO_HIDE_MS}ms linear forwards`,
      }} />
    </div>
  );
};

export default GlobalPushBanner;
